import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FormattedMessage } from '../../util/reactIntl';
import { blockUser, unblockUser, getBlockedUserIds } from '../../ducks/user.duck';

import { InlineTextButton } from '../Button/Button';

import css from './BlockUserButton.module.css';

/**
 * A small toggle button that lets the current user block or unblock another user.
 * Blocking is private: the blocked user is never notified, and nothing is sent
 * to the marketplace operator. It only affects what the blocking user sees
 * (that user's listings and conversation get hidden for the blocker).
 *
 * @component
 * @param {Object} props
 * @param {string} props.otherUserId - The UUID string of the user to block/unblock
 * @param {string} [props.className]
 * @returns {JSX.Element|null}
 */
const BlockUserButton = props => {
  const { otherUserId, className } = props;
  const dispatch = useDispatch();
  const currentUser = useSelector(state => state.user?.currentUser);
  const [inProgress, setInProgress] = useState(false);
  const [error, setError] = useState(false);

  if (!currentUser?.id || !otherUserId || currentUser.id.uuid === otherUserId) {
    return null;
  }

  const isBlocked = getBlockedUserIds(currentUser).includes(otherUserId);

  const handleClick = () => {
    setError(false);
    setInProgress(true);
    const action = isBlocked ? unblockUser(otherUserId) : blockUser(otherUserId);
    dispatch(action)
      .then(() => setInProgress(false))
      .catch(() => {
        setInProgress(false);
        setError(true);
      });
  };

  return (
    <div className={className}>
      <InlineTextButton
        type="button"
        className={css.button}
        onClick={handleClick}
        disabled={inProgress}
      >
        {isBlocked ? (
          <FormattedMessage id="BlockUserButton.unblock" />
        ) : (
          <FormattedMessage id="BlockUserButton.block" />
        )}
      </InlineTextButton>
      {error ? (
        <p className={css.error}>
          <FormattedMessage id="BlockUserButton.genericError" />
        </p>
      ) : null}
    </div>
  );
};

export default BlockUserButton;
